/**
 * Auth REST client: talks to the Insights backend's /api/auth endpoints. All
 * requests carry the httpOnly session cookie via `credentials: 'include'`.
 */
import { localeHeaders } from '../i18n';
import i18n from '../i18n';
import { ApiError } from './api';

const API_BASE = (import.meta.env.VITE_API_BASE as string | undefined) ?? '';

export type AuthUser = {
  id: string;
  email: string;
  isSuperUser: boolean;
};

export type MeResponse = {
  authDisabled: boolean;
  user: AuthUser | null;
  restaurants: { id: string }[];
};

async function parseJson<T>(res: Response): Promise<T> {
  try {
    return (await res.json()) as T;
  } catch {
    throw new ApiError(
      i18n.t('errors:requestFailed', { status: res.status }),
      undefined,
      res.status,
    );
  }
}

export async function fetchMe(): Promise<MeResponse | null> {
  const res = await fetch(`${API_BASE}/api/auth/me`, {
    method: 'GET',
    credentials: 'include',
    headers: { Accept: 'application/json', ...localeHeaders() },
  });
  if (res.status === 401) {
    return null;
  }
  if (!res.ok) {
    throw new ApiError(
      i18n.t('errors:requestFailed', { status: res.status }),
      undefined,
      res.status,
    );
  }
  return parseJson<MeResponse>(res);
}

export async function login(email: string, password: string): Promise<MeResponse> {
  const res = await fetch(`${API_BASE}/api/auth/login`, {
    method: 'POST',
    credentials: 'include',
    headers: { 'Content-Type': 'application/json', ...localeHeaders() },
    body: JSON.stringify({ email, password }),
  });
  if (!res.ok) {
    const body = await parseJson<{ detail?: string; error?: string }>(res).catch(
      () => ({}) as { detail?: string; error?: string },
    );
    const message =
      res.status === 401
        ? i18n.t('login:invalidCredentials')
        : body.detail ||
          body.error ||
          i18n.t('login:failedStatus', { status: res.status });
    throw new ApiError(message, undefined, res.status);
  }
  return parseJson<MeResponse>(res);
}

export async function logout(): Promise<void> {
  await fetch(`${API_BASE}/api/auth/logout`, {
    method: 'POST',
    credentials: 'include',
  }).catch(() => undefined);
}
